import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";

const HINTS = {
  json: '[{"term": "…", "definition": "…", "chapter_reference": "Chapter 1.2", "entry_attribution": ""}]',
  csv: "term,definition,chapter_reference,entry_attribution",
};

export const EntryImportDialog = ({ pack, onOpenChange, onDone }) => {
  const [format, setFormat] = useState("pressbooks_url");
  const [content, setContent] = useState("");
  const [mode, setMode] = useState("replace");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/packs/${pack.id}/import`, { format, content, mode });
      setResult(data);
      toast.success(`Imported ${data.imported} entries`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!pack} onOpenChange={(o) => { if (!o) { setResult(null); setContent(""); } onOpenChange(o); }}>
      <DialogContent data-testid="entry-import-dialog" className="bg-white sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import source entries</DialogTitle>
          <DialogDescription>{pack?.title}. Definitions are saved exactly as provided. Nothing is rewritten.</DialogDescription>
        </DialogHeader>
        <Tabs value={format} onValueChange={setFormat}>
          <TabsList>
            <TabsTrigger value="pressbooks_url" data-testid="import-format-url">Pressbooks glossary URL</TabsTrigger>
            <TabsTrigger value="json" data-testid="import-format-json">JSON</TabsTrigger>
            <TabsTrigger value="csv" data-testid="import-format-csv">CSV</TabsTrigger>
          </TabsList>
        </Tabs>
        {format === "pressbooks_url"
          ? <Input data-testid="import-content-input" placeholder={pack?.source_url || "https://…/back-matter/glossary/"} value={content} onChange={(e) => setContent(e.target.value)} />
          : <Textarea data-testid="import-content-textarea" rows={10} className="font-mono text-xs" placeholder={HINTS[format]} value={content} onChange={(e) => setContent(e.target.value)} />}
        <div className="flex items-center gap-4 text-sm">
          {["replace", "append"].map((m) => (
            <label key={m} className="flex items-center gap-2 capitalize">
              <input type="radio" checked={mode === m} onChange={() => setMode(m)} data-testid={`import-mode-${m}`} />{m === "replace" ? "Replace all entries" : "Append"}
            </label>
          ))}
        </div>
        {result && (
          <div data-testid="import-result" className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            Imported {result.imported}, skipped {result.skipped}. Pack now has {result.entry_count} entries (status: {result.pack.status}).
            {result.duplicate_terms.length > 0 && <div className="mt-1 text-amber-800">Terms that appear more than once in the source: {result.duplicate_terms.join(", ")}</div>}
          </div>
        )}
        <DialogFooter>
          <Button data-testid="import-submit-button" disabled={busy || !content.trim()} onClick={submit} className="rounded-full px-6">{busy ? "Importing…" : "Import entries"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
